import {FormProvider, useForm} from "react-hook-form";
import {useSelector} from "react-redux";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import cn from "classnames";
import {boolean, number, object, string} from "yup";

import {Button} from "@pg-design/button-module";
import {Link} from "@pg-design/link-module";
import {CalendarSliderControlled} from "@pg-design/rhf-components-module";
import {InputField, MaxValueFieldControlled, PhoneFieldControlled} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {OFFER_TEST_IDS} from "@pg-mono/e2e-test-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../../../../../../app/rp_reducer";
import {validationMessages} from "../../../../../../../form/utils/validation_messages";
import {useShouldShowPrice} from "../../../../../../../hooks/use_should_show_price";
import {useGetOfferDetailApplicationQuery} from "../../../../../../../offer/api/get_offer_detail";
import {IOfferDetailApplication} from "../../../../../../../offer/types/IOfferDetailApplication";
import {useAppSelector} from "../../../../../../../store/hooks";
import {UserMktOptins} from "../../../../../../../user/components/UserMktOptins";
import {getCurrency} from "../../../../../../../utils/get_currency";
import {isValidationError} from "../../../../../../../utils/isValidationError";
import {useGetVendorDetailApplicationQuery} from "../../../../../../../vendor/api/get_vendor_detail_application_api";
import {askForPriceleadButtonCopy} from "../../../../../../constants/lead_button_copy";
import {useOpenedLeadModalRequiredParams} from "../../../../../../hooks/use_lead_modal_required_params";
import {AdditionalFormFieldType} from "../../../../../../types/AdditionalFormFieldType";
import {ILeadModalPriceConversationChatLeadFormHandleSubmit} from "../../../../../../types/ILeadModalPriceConversationChatLeadFormHandleSubmit";
import {ILeadModalPriceConversationChatLeadFormValues} from "../../../../../../types/ILeadModalPriceConversationChatLeadFormValues";
import {ILeadModalPriceConversationPrice} from "../../../../../../types/ILeadModalPriceConversationPrice";
import {LeadButtonTerms} from "../../../../../LeadButtonTerms";
import {LeadRodoTerms} from "../../../../../LeadRodoTerms";
import {LeadVendorRodoTerms} from "../../../../../LeadVendorRodoTerms";

interface IProps {
    showForm: boolean;
    hideRodoTerms?: boolean;
    initialValues: ILeadModalPriceConversationChatLeadFormValues;
    disabled: boolean;
    handleSubmit: ILeadModalPriceConversationChatLeadFormHandleSubmit;
    handleFormInteraction: () => void;
    handleChangeData: () => void;
    price: ILeadModalPriceConversationPrice | null;
    offer?: IOfferDetailApplication;
}

export function LeadModalPriceConversationChatLeadForm(props: IProps) {
    const {hideRodoTerms, initialValues, disabled, handleSubmit, handleFormInteraction, showForm, handleChangeData} = props;

    const {isMobile} = useUserDevice();

    const {vendorId, offerId, source, sourceSection} = useOpenedLeadModalRequiredParams("property");
    const {data: vendorDetail} = useGetVendorDetailApplicationQuery(vendorId);
    const {data: offerDetail} = useGetOfferDetailApplicationQuery(offerId as number, {skip: !offerId});

    const codes = useSelector((state: IRPStore) => state.variables.data?.country_codes);
    const additionalFieldInUse = useAppSelector((state) => state.leadModal.useAdditionalFormField);
    const hoursOptions = useAppSelector((state) => state.leadModal.hourOptions);
    const currency = useAppSelector((state) => state.leadModal.currency);

    const defaultValues = {
        ...initialValues,
        ...(additionalFieldInUse === AdditionalFormFieldType.NEGOTIATION ? {negotiation: props.price?.max} : {}),
        ...(additionalFieldInUse === AdditionalFormFieldType.MEETING ? {meetingDay: undefined} : {})
    } as ILeadModalPriceConversationChatLeadFormValues;

    const form = useForm<ILeadModalPriceConversationChatLeadFormValues>({
        disabled,
        defaultValues,
        resolver: yupResolver(additionalFieldInUse === AdditionalFormFieldType.MEETING ? meetingDaySchema : formSchema),
        mode: "onBlur"
    });

    const shouldShowPrice = useShouldShowPrice({
        vendorId: vendorDetail?.id || 0,
        offerPriceVisibility: offerDetail?.configuration?.price_visibility,
        vendorPriceVisibility: vendorDetail?.configuration?.price_visibility
    });

    const onSubmit = async (formValues: ILeadModalPriceConversationChatLeadFormValues) => {
        handleSubmit(formValues, (error) => {
            //show form when logged user has blocked email
            if (isValidationError(error) && !showForm) {
                handleChangeData();
            }
            if (isValidationError(error)) {
                for (const fieldName of Object.keys(error.appError.fieldErrors)) {
                    const errorMessage = error.appError.fieldErrors[fieldName];
                    form.setError(fieldName as keyof ILeadModalPriceConversationChatLeadFormValues, {
                        message: errorMessage.join(", ")
                    });
                }
            }
        });
    };

    const leadButtonText = props.price ? "Prześlij zapytanie" : askForPriceleadButtonCopy;

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className={fields}>
                {(!showForm && !initialValues) || showForm ? (
                    <>
                        <InputField
                            name="name"
                            placeholder="Imię i nazwisko"
                            onClick={handleFormInteraction}
                            dataTestId={OFFER_TEST_IDS.LEAD_FORM.FORM_NAME_INPUT}
                        />
                        <InputField name="email" placeholder="E-mail" onClick={handleFormInteraction} dataTestId={OFFER_TEST_IDS.LEAD_FORM.FORM_EMAIL_INPUT} />
                        <PhoneFieldControlled
                            name="phone"
                            codes={codes ?? []}
                            onClick={handleFormInteraction}
                            placeholder="Numer telefonu"
                            maxMenuHeight={200}
                            dataTestId={OFFER_TEST_IDS.LEAD_FORM.FORM_PHONE_INPUT}
                        />
                    </>
                ) : (
                    <div className={filledData}>
                        <Text variant="mini_header">Twoje dane</Text>
                        <Text variant="body_copy_2">
                            Imię i nazwisko: <strong>{initialValues.name}</strong>
                            <br />
                            e-mail: <strong>{initialValues.email}</strong>
                            <br />
                            Telefon: <strong>{initialValues.phone.number}</strong>
                        </Text>
                        <Link className={linkDecoration} onClick={handleChangeData}>
                            Zmień dane
                        </Link>
                    </div>
                )}
                {additionalFieldInUse === AdditionalFormFieldType.NEGOTIATION && props.price && (
                    <MaxValueFieldControlled
                        max={props.price.max}
                        min={Math.ceil(props.price.max * 0.9)}
                        step={1000}
                        name="negotiation"
                        defaultValue={props.price.max}
                        useTriColorSlider
                        inputClassName={inputClass}
                        rightElement={getCurrency({currency})}
                        className={css({pl: 1.5, mt: 1})}
                        labelContent="Twoja propozycja ceny"
                    />
                )}
                {additionalFieldInUse === AdditionalFormFieldType.MEETING && (
                    <CalendarSliderControlled name="meetingDay" headline="Zaproponuj datę spotkania" className={cn(sliderClass)} hourOptions={hoursOptions} />
                )}

                <UserMktOptins showForm={showForm} initialValues={initialValues} className={optins} formId={`${source}_${sourceSection}_lead_modal`} />
                <Button
                    variant="filled_primary"
                    type="submit"
                    onClick={handleFormInteraction}
                    disabled={disabled}
                    isLoading={disabled}
                    dataTestId={OFFER_TEST_IDS.LEAD_FORM.SEND_LEAD_BUTTON}
                >
                    {shouldShowPrice ? "Prześlij zapytanie" : askForPriceleadButtonCopy}
                </Button>
                <div className={leadTerms}>
                    <LeadButtonTerms buttonText={leadButtonText} />
                    {isMobile && !hideRodoTerms && (
                        <>
                            <LeadRodoTerms />
                            {vendorDetail && (
                                <LeadVendorRodoTerms
                                    vendor={vendorDetail}
                                    buttonText={leadButtonText}
                                    offerPrivacyPolicyUrl={offerDetail?.configuration.privacy_policy_url}
                                    offerPersonalDataProcessorName={offerDetail?.configuration.personal_data_processor_name}
                                    offerPersonalDataProcessorUrl={offerDetail?.configuration.personal_data_processor_url}
                                />
                            )}
                        </>
                    )}
                </div>
            </form>
        </FormProvider>
    );
}

// Utils
const formSchema = object().shape({
    name: string().required(),
    email: string().required().email(),
    phone: object().shape({code: number().required(), number: string().required()}),
    marketing_email: boolean().required().oneOf([true], validationMessages.required)
});

const meetingDaySchema = object().shape({
    name: string().required(),
    email: string().required().email(),
    phone: object().shape({code: number().required(), number: string().required()}),
    marketing_email: boolean().required().oneOf([true], validationMessages.required),
    meetingDay: object().required("Proszę wybrać datę")
});

// Styles
const fields = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});

const optins = css({
    p: 0,
    py: 1
});

const linkDecoration = css({
    textDecoration: "underline"
});

const filledData = css({
    backgroundColor: "#fafaf5",
    p: 2,
    display: "flex",
    flexDirection: "column",
    gap: 1,
    borderRadius: 2
});

const sliderClass = css({
    margin: "auto",
    maxWidth: "334px"
});

const inputClass = css({
    maxWidth: "80px",
    overflow: "hidden"
});

const leadTerms = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    pb: 2,
    pt: 2
});
import {FormProvider, useForm} from "react-hook-form";
import {useSelector} from "react-redux";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";
import cn from "classnames";
import {boolean, number, object, string} from "yup";

import {Button} from "@pg-design/button-module";
import {Link} from "@pg-design/link-module";
import {CalendarSliderControlled} from "@pg-design/rhf-components-module";
import {InputField, MaxValueFieldControlled, PhoneFieldControlled} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {OFFER_TEST_IDS} from "@pg-mono/e2e-test-utils";
import {useUserDevice} from "@pg-mono/user-device";

import {IRPStore} from "../../../../../../../app/rp_reducer";
import {validationMessages} from "../../../../../../../form/utils/validation_messages";
import {useShouldShowPrice} from "../../../../../../../hooks/use_should_show_price";
import {useGetOfferDetailApplicationQuery} from "../../../../../../../offer/api/get_offer_detail";
import {IOfferDetailApplication} from "../../../../../../../offer/types/IOfferDetailApplication";
import {useAppSelector} from "../../../../../../../store/hooks";
import {UserMktOptins} from "../../../../../../../user/components/UserMktOptins";
import {getCurrency} from "../../../../../../../utils/get_currency";
import {isValidationError} from "../../../../../../../utils/isValidationError";
import {useGetVendorDetailApplicationQuery} from "../../../../../../../vendor/api/get_vendor_detail_application_api";
import {askForPriceleadButtonCopy} from "../../../../../../constants/lead_button_copy";
import {useOpenedLeadModalRequiredParams} from "../../../../../../hooks/use_lead_modal_required_params";
import {AdditionalFormFieldType} from "../../../../../../types/AdditionalFormFieldType";
import {ILeadModalPriceConversationChatLeadFormHandleSubmit} from "../../../../../../types/ILeadModalPriceConversationChatLeadFormHandleSubmit";
import {ILeadModalPriceConversationChatLeadFormValues} from "../../../../../../types/ILeadModalPriceConversationChatLeadFormValues";
import {ILeadModalPriceConversationPrice} from "../../../../../../types/ILeadModalPriceConversationPrice";
import {LeadButtonTerms} from "../../../../../LeadButtonTerms";
import {LeadRodoTerms} from "../../../../../LeadRodoTerms";
import {LeadVendorRodoTerms} from "../../../../../LeadVendorRodoTerms";

interface IProps {
    showForm: boolean;
    hideRodoTerms?: boolean;
    initialValues: ILeadModalPriceConversationChatLeadFormValues;
    disabled: boolean;
    handleSubmit: ILeadModalPriceConversationChatLeadFormHandleSubmit;
    handleFormInteraction: () => void;
    handleChangeData: () => void;
    price: ILeadModalPriceConversationPrice | null;
    offer?: IOfferDetailApplication;
}

export function LeadModalPriceConversationChatLeadForm(props: IProps) {
    const {hideRodoTerms, initialValues, disabled, handleSubmit, handleFormInteraction, showForm, handleChangeData} = props;

    const {isMobile} = useUserDevice();

    const {vendorId, offerId, source, sourceSection} = useOpenedLeadModalRequiredParams("property");
    const {data: vendorDetail} = useGetVendorDetailApplicationQuery(vendorId);
    const {data: offerDetail} = useGetOfferDetailApplicationQuery(offerId as number, {skip: !offerId});

    const codes = useSelector((state: IRPStore) => state.variables.data?.country_codes);
    const additionalFieldInUse = useAppSelector((state) => state.leadModal.useAdditionalFormField);
    const hoursOptions = useAppSelector((state) => state.leadModal.hourOptions);
    const currency = useAppSelector((state) => state.leadModal.currency);

    const defaultValues = {
        ...initialValues,
        ...(additionalFieldInUse === AdditionalFormFieldType.NEGOTIATION ? {negotiation: props.price?.max} : {}),
        ...(additionalFieldInUse === AdditionalFormFieldType.MEETING ? {meetingDay: undefined} : {})
    } as ILeadModalPriceConversationChatLeadFormValues;

    const form = useForm<ILeadModalPriceConversationChatLeadFormValues>({
        disabled,
        defaultValues,
        resolver: yupResolver(additionalFieldInUse === AdditionalFormFieldType.MEETING ? meetingDaySchema : formSchema),
        mode: "onBlur"
    });

    const shouldShowPrice = useShouldShowPrice({
        vendorId: vendorDetail?.id || 0,
        offerPriceVisibility: offerDetail?.configuration?.price_visibility,
        vendorPriceVisibility: vendorDetail?.configuration?.price_visibility
    });

    const onSubmit = async (formValues: ILeadModalPriceConversationChatLeadFormValues) => {
        handleSubmit(formValues, (error) => {
            //show form when logged user has blocked email
            if (isValidationError(error) && !showForm) {
                handleChangeData();
            }
            if (isValidationError(error)) {
                for (const fieldName of Object.keys(error.appError.fieldErrors)) {
                    const errorMessage = error.appError.fieldErrors[fieldName];
                    form.setError(fieldName as keyof ILeadModalPriceConversationChatLeadFormValues, {
                        message: errorMessage.join(", ")
                    });
                }
            }
        });
    };

    const leadButtonText = props.price ? "Prześlij zapytanie" : askForPriceleadButtonCopy;

    return (
        <FormProvider {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className={fields}>
                {(!showForm && !initialValues) || showForm ? (
                    <>
                        <InputField
                            name="name"
                            placeholder="Imię i nazwisko"
                            onClick={handleFormInteraction}
                            dataTestId={OFFER_TEST_IDS.LEAD_FORM.FORM_NAME_INPUT}
                        />
                        <InputField name="email" placeholder="E-mail" onClick={handleFormInteraction} dataTestId={OFFER_TEST_IDS.LEAD_FORM.FORM_EMAIL_INPUT} />
                        <PhoneFieldControlled
                            name="phone"
                            codes={codes ?? []}
                            onClick={handleFormInteraction}
                            placeholder="Numer telefonu"
                            maxMenuHeight={200}
                            dataTestId={OFFER_TEST_IDS.LEAD_FORM.FORM_PHONE_INPUT}
                        />
                    </>
                ) : (
                    <div className={filledData}>
                        <Text variant="mini_header">Twoje dane</Text>
                        <Text variant="body_copy_2">
                            Imię i nazwisko: <strong>{initialValues.name}</strong>
                            <br />
                            e-mail: <strong>{initialValues.email}</strong>
                            <br />
                            Telefon: <strong>{initialValues.phone.number}</strong>
                        </Text>
                        <Link className={linkDecoration} onClick={handleChangeData}>
                            Zmień dane
                        </Link>
                    </div>
                )}
                {additionalFieldInUse === AdditionalFormFieldType.NEGOTIATION && props.price && (
                    <MaxValueFieldControlled
                        max={props.price.max}
                        min={Math.ceil(props.price.max * 0.9)}
                        step={1000}
                        name="negotiation"
                        defaultValue={props.price.max}
                        useTriColorSlider
                        inputClassName={inputClass}
                        rightElement={getCurrency({currency})}
                        className={css({pl: 1.5, mt: 1})}
                        labelContent="Twoja propozycja ceny"
                    />
                )}
                {additionalFieldInUse === AdditionalFormFieldType.MEETING && (
                    <CalendarSliderControlled name="meetingDay" headline="Zaproponuj datę spotkania" className={cn(sliderClass)} hourOptions={hoursOptions} />
                )}

                <UserMktOptins showForm={showForm} initialValues={initialValues} className={optins} formId={`${source}_${sourceSection}_lead_modal`} />
                <Button
                    variant="filled_primary"
                    type="submit"
                    onClick={handleFormInteraction}
                    disabled={disabled}
                    isLoading={disabled}
                    dataTestId={OFFER_TEST_IDS.LEAD_FORM.SEND_LEAD_BUTTON}
                >
                    {shouldShowPrice ? "Prześlij zapytanie" : askForPriceleadButtonCopy}
                </Button>
                <div className={leadTerms}>
                    <LeadButtonTerms buttonText={leadButtonText} />
                    {isMobile && !hideRodoTerms && (
                        <>
                            <LeadRodoTerms />
                            {vendorDetail && (
                                <LeadVendorRodoTerms
                                    vendor={vendorDetail}
                                    buttonText={leadButtonText}
                                    offerPrivacyPolicyUrl={offerDetail?.configuration.privacy_policy_url}
                                    offerPersonalDataProcessorName={offerDetail?.configuration.personal_data_processor_name}
                                    offerPersonalDataProcessorUrl={offerDetail?.configuration.personal_data_processor_url}
                                />
                            )}
                        </>
                    )}
                </div>
            </form>
        </FormProvider>
    );
}

// Utils
const formSchema = object().shape({
    name: string().required(),
    email: string().required().email(),
    phone: object().shape({code: number().required(), number: string().required()}),
    marketing_email: boolean().required().oneOf([true], validationMessages.required)
});

const meetingDaySchema = object().shape({
    name: string().required(),
    email: string().required().email(),
    phone: object().shape({code: number().required(), number: string().required()}),
    marketing_email: boolean().required().oneOf([true], validationMessages.required),
    meetingDay: object().required("Proszę wybrać datę")
});

// Styles
const fields = css({
    display: "flex",
    flexDirection: "column",
    gap: 1
});

const optins = css({
    p: 0,
    py: 1
});

const linkDecoration = css({
    textDecoration: "underline"
});

const filledData = css({
    backgroundColor: "#fafaf5",
    p: 2,
    display: "flex",
    flexDirection: "column",
    gap: 1,
    borderRadius: 2
});

const sliderClass = css({
    margin: "auto",
    maxWidth: "334px"
});

const inputClass = css({
    maxWidth: "80px",
    overflow: "hidden"
});

const leadTerms = css({
    display: "flex",
    flexDirection: "column",
    gap: 2,
    pb: 2,
    pt: 2
});
